import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Box,
  Container, 
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Button,
  CircularProgress,
  Chip,
  Paper
} from '@mui/material';
import { 
  Person, 
  Receipt,
  LocalShipping,
  AccessTime,
  SupervisorAccount
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { getUserOrders } from '../../services/orderService';
import { getOrderStatusLabelKey, getStatusColor } from '../../utils/orderStatusConfirm';
import AccountPageShell from '../Account/AccountPageShell';
import { COLORS, FONTS } from '../../theme/tokens';
import { PANEL_SX, SECONDARY_BUTTON_SX } from '../../theme/v2Styles';
const Dashboard = () => {
  const { t } = useTranslation();
  const { user, loading, isAuthenticated } = useContext(AuthContext);
  const [orders, setOrders] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  useEffect(() => {
    const fetchOrders = async () => {
      if (!loading && isAuthenticated && user) {
        try {
          setLoadingData(true);
          const data = await getUserOrders();
          // Sort orders by creation date, newest first
          const sortedOrders = data.sort((a, b) => 
            new Date(b.order_created_at) - new Date(a.order_created_at)
          );
          setOrders(sortedOrders);
        } catch (err) {} finally {
          setLoadingData(false);
        }
      }
    };
    fetchOrders();
  }, [user, loading, isAuthenticated]);
  if (loading || !user) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }
  return (
    <AccountPageShell title={t('account.myAccount')}>
      <Grid container spacing={3}>
        {/* Welcome Card */}
        <Grid item xs={12}>
          <Paper elevation={0} sx={{ ...PANEL_SX, p: { xs: 2.5, md: 3 }, mb: 1, bgcolor: COLORS.surfaceWarm }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography sx={{ fontFamily: FONTS.heading, fontWeight: 600, fontSize: '1.5rem', color: COLORS.textPrimary }}>
                  {t('account.welcomeBack', { name: `${user.firstname} ${user.lastname}` })}
                </Typography>
                <Typography variant="body1" sx={{ mt: 1, color: COLORS.textBody }}>
                  {t('account.overviewIntro')}
                </Typography>
              </Box>
              {/* Admin Actions */}
              {user.is_admin >= 1 && (
                <Button
                  component={Link}
                  to="/admin"
                  variant="contained"
                  color="secondary"
                  startIcon={<SupervisorAccount />}
                  sx={SECONDARY_BUTTON_SX}
                >
                  Admin Dashboard
                </Button>
              )}
            </Box>
          </Paper>
        </Grid>
        {/* User Information */}
        <Grid item xs={12} md={4}>
          <Card elevation={0} sx={{ ...PANEL_SX, height: '100%' }}>
            <CardHeader
              title={t('account.accountDetails')}
              avatar={<Person color="primary" />}
              action={
                <Button 
                  component={Link} 
                  to="/profile" 
                  size="small" 
                  color="primary"
                  variant="outlined"
                  startIcon={<Person />}
                >
                  {t('account.editProfile')}
                </Button>
              }
            />
            <Divider />
            <CardContent>
              <List dense>
                <ListItem>
                  <Box sx={{ width: '100%' }}>
                    <Typography variant="subtitle2" component="span">{t('account.name')}</Typography>
                    <Typography variant="body2" component="div" color="text.secondary" sx={{ mt: 0.5 }}>
                      {`${user.firstname} ${user.lastname}`}
                    </Typography>
                  </Box>
                </ListItem>
                <ListItem>
                  <Box sx={{ width: '100%' }}>
                    <Typography variant="subtitle2" component="span">{t('account.email')}</Typography>
                    <Typography variant="body2" component="div" color="text.secondary" sx={{ mt: 0.5 }}>
                      {user.email}
                    </Typography>
                  </Box>
                </ListItem>
                {user.phonenumber && (
                  <ListItem>
                    <Box sx={{ width: '100%' }}>
                      <Typography variant="subtitle2" component="span">{t('account.phone')}</Typography>
                      <Typography variant="body2" component="div" color="text.secondary" sx={{ mt: 0.5 }}>
                        {user.phonenumber}
                      </Typography>
                    </Box>
                  </ListItem>
                )}
                {user.address && (
                  <ListItem>
                    <Box sx={{ width: '100%' }}>
                      <Typography variant="subtitle2" component="span">{t('account.address')}</Typography>
                      <Typography variant="body2" component="div" color="text.secondary" sx={{ mt: 0.5 }}>
                        {`${user.address}${user.city ? ', ' + user.city : ''}${user.postcode ? ' ' + user.postcode : ''}`}
                      </Typography>
                    </Box>
                  </ListItem>
                )}
              </List>
            </CardContent>
          </Card>
        </Grid>
        {/* Recent Orders */}
        <Grid item xs={12} md={8}>
          <Card elevation={0} sx={PANEL_SX}>
            <CardHeader 
              title={`${t('account.recentOrders')}${orders.length > 0 ? ` (${orders.length})` : ''}`}
              avatar={<Receipt color="primary" />}
              action={
                <Button
                  component={Link}
                  to="/my-orders"
                  size="small"
                  color="primary"
                  variant="contained"
                  startIcon={<Receipt />}
                >
                  {t('account.viewAll')}
                </Button>
              }
            />
            <Divider />
            <CardContent>
              {loadingData ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : orders.length > 0 ? (
                <List>
                  {orders.slice(0, 3).map(order => (
                    <ListItem 
                      key={order.order_id} 
                      divider 
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        gap: 1
                      }}
                    >
                      <ListItemIcon>
                        <LocalShipping color={getStatusColor(order.status_id)} />
                      </ListItemIcon>
                      {/* Replace ListItemText with custom Box to avoid nesting issues */}
                      <Box sx={{ 
                        flex: 1, 
                        minWidth: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center'
                      }}>
                        {/* Primary content */}
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          flexWrap: 'wrap', 
                          gap: 1,
                          mb: 0.5
                        }}>
                          <Typography variant="subtitle2" component="span">
                            {order.order_number}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <AccessTime fontSize="small" sx={{ mr: 0.5, opacity: 0.7, fontSize: '0.75rem' }} />
                            <Typography variant="caption" color="text.secondary" component="span">
                              {formatDate(order.order_created_at)}
                            </Typography>
                          </Box>
                        </Box>
                        
                        {/* Secondary content */}
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          flexWrap: 'wrap', 
                          gap: 1 
                        }}>
                          <Chip 
                            label={t(getOrderStatusLabelKey(order.status_id))} 
                            color={getStatusColor(order.status_id)} 
                            size="small"
                          />
                          <Typography variant="body2" component="span" sx={{ fontWeight: 'medium' }}>
                            €{order.order_total ? parseFloat(order.order_total).toFixed(2) : '0.00'}
                          </Typography>
                        </Box>
                      </Box>
                      <Button 
                        component={Link} 
                        to={`/orders/${order.order_id}`} 
                        size="small"
                        variant="outlined"
                        sx={{ minWidth: '100px' }}
                      >
                        {t('account.details')}
                      </Button>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" sx={{ p: 2, textAlign: 'center' }}>
                  {t('account.noOrdersYet')}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </AccountPageShell>
  );
};
export default Dashboard;
